/**
 * The **event-family model** — the ~11-member family taxonomy + a per-family typed **`match`**
 * pattern + the evaluator that reads a `match` against a {@link TriggerEvent} payload.
 *
 * **The live vocabulary.** `TriggeredEffect`/`ModifierEffect` (`atoms.ts`) carry
 * `on: TriggerFamily` + optional `match: TriggerMatch`; `fireTrigger` (`triggerBus.ts`) gathers
 * by family, then calls {@link evaluateMatch} before `condition`.
 *
 * **The family taxonomy.** `on` is exactly the family string — sub-discriminants
 * (`phase`/`change`/`track`) live in the typed event payload / `match`, never in `on`,
 * because INERT keys at *family* granularity (a `match` can't be inert, only a family can).
 * The one exception is the turn/round edge, which is part of the family name
 * (`start_turn`/`end_turn`/`start_round`/`end_round`, ruled by the developer 2026-09-06): an
 * author writes the moment they mean as one name, not a family plus an `edge` field.
 *
 * **The `match` shape.** A flat, **conjunctive** record pattern, typed per family (a
 * discriminated union on `family`, mirroring {@link TriggerEvent}'s own discriminant so
 * {@link evaluateMatch} can narrow both sides together): booleans compare by strict equality
 * (`hit: false` = miss — see {@link evaluateMatch}'s own doc for why absent does NOT mean
 * false here), `string | string[]` is any-of, and a scalar takes either a literal (exact-equal)
 * or a **comparator object** keyed by {@link CmpOp} (`{ ">": 1 }`). A wrong-family field is
 * structurally unparseable once `match` is attached to a real clause — the never-true class
 * dies by the type system, not a runtime gate. `match` is conjunctive-only (a known, accepted
 * cost): a disjunction spanning event and state facts is two clauses in the entry's
 * `effects[]`, not expressible in one `match`.
 */

import type { AttackVerb, DamageType, EarmarkVerb } from "@lancer-vtt/domain";
import type { CmpOp } from "./compare";
import { compare } from "./compare";
import type { ActionEvent, AttackEvent, AuraEvent, AuraMoment, CoverDegree, DamageEvent, MoveEvent, SaveEvent, StatusEvent, TriggerEvent } from "./predicate";

// ── the family taxonomy ────────────────────────────────────────────────────────────

/**
 * The ~12 pipeline-moment families. Interception windows (the reaction registry,
 * `on_would_be_destroyed`-class) are deliberately NOT here — they change control flow and stay
 * their own mechanism.
 *
 * **`"trample"`** (`npc-features-fielded-roster` Phase 5.1) fires ONCE per character whose
 * occupied cell a Trampling mover's committed path crossed, `self` = the mover, `target` = that
 * character — the sibling family to `"move"` (which fires ONCE for the mover itself, `self` =
 * `target` = the mover). Deliberately its OWN family rather than a `"move"` match variant: a
 * `"move"` clause's automatic self-fire (`resolve/triggers.ts`'s `resolveMoveTrigger`) gathers
 * every on-move handler the mover carries unconditionally, and reusing that family for a
 * different target binding risked an unrelated on-move clause (e.g. a self-clearing "moved more
 * than 1 space" marker) firing against the wrong actor. See `resolve/triggers.ts`'s
 * `resolveTrampleTrigger` for the fire site and `referee/moveFollowups.ts`'s `trampleInto` for
 * the sweep that calls it once per crossed character.
 *
 * **A watcher's reaction to someone else's move** is a `"move"` clause marked `bystander: true`
 * (Rapid Response: "an enemy character in line of sight moves"), not a family of its own: the
 * observed-move window (`observedMove.ts`) offers it after the movement, `self` = the watcher and
 * `target` = the mover, and `resolveMoveTrigger` skips it, so the mover-binding hazard above can't
 * arise. The flag already names an observer's clause on the attack family.
 *
 * **The four lifecycle families** (`start_turn`/`end_turn`/`start_round`/`end_round`) name the
 * edge in the family itself. **Word-order trap:** the internal, never-authored
 * `LifecycleTriggerPoint` (`resolve/triggers.ts`) spells the same four moments the other way
 * round (`turn_start`, …), and `resolve/triggers.ts`'s `lifecycleEventFor` is the one place that
 * maps between them. Authored content always uses these names. A turn family fires for the
 * activating/ending unit only (its carrier is that unit), so there is no "on someone else's turn"
 * form; that would be a fire-site change, not a match field.
 *
 * **`"aura"`** (`ll1-drones` D4, `decisions/0201`) fires a deployed drone's field on one character
 * in it: `self` = the deployer, `target` = the character, `site` = the drone's space. It is
 * gathered off the drone's `deployedFrom.entryId` only (`resolve/triggers.ts`'s
 * `resolveAuraTrigger`), never off a carrier walk, and a clause must name its moment in `match`.
 */
export type TriggerFamily =
  | "attack"
  | "damage"
  | "heat"
  | "track_damage"
  | "destruction"
  | "status"
  | "save"
  | "action"
  | "move"
  | "trample"
  | "aura"
  | "start_turn"
  | "end_turn"
  | "start_round"
  | "end_round";

// ── scalar/string match field helpers ──────────────────────────────────────────────

/**
 * A scalar `match` field: either a literal (exact-equal, the common case — `dc: 11`) or a
 * **comparator object** keyed by exactly one {@link CmpOp} (`{ ">": 1 }`, `t_heavy_gunner:1`'s
 * "moves more than 1 space"). Evaluated by {@link matchScalar}.
 */
export type MatchScalar = number | { [K in CmpOp]?: number };

/**
 * A string `match` field: a literal (exact-equal) or an array (**any-of**). Evaluated by
 * {@link matchStringField}.
 */
export type MatchStringField = string | string[];

/** Evaluate a {@link MatchScalar} pattern against an actual numeric fact. `undefined` actual never matches (the fact wasn't set on this event). */
export function matchScalar(actual: number | undefined, pattern: MatchScalar): boolean {
  if (actual === undefined) return false;
  if (typeof pattern === "number") return actual === pattern;
  return (Object.keys(pattern) as CmpOp[]).every((op) => compare(actual, op, pattern[op]!));
}

/** Evaluate a {@link MatchStringField} pattern against an actual string fact. `undefined` actual never matches. */
export function matchStringField(actual: string | undefined, pattern: MatchStringField): boolean {
  if (actual === undefined) return false;
  return Array.isArray(pattern) ? pattern.includes(actual) : actual === pattern;
}

// ── per-family match patterns (a discriminated union on `family`) ──────────────────

/**
 * The attack-family `match` pattern — every fact an attack fire carries: the declaration's
 * static facets (`ranged`/`tech`/`weaponType`/`mount`/`verb`/`actionId`) plus the roll's
 * outcome (`hit`/`crit`/`natural20`/`rawD20`/`firstAttackThisTurn`). `hit`/`crit` are booleans,
 * not a 3-state outcome — "on miss" is `{ hit: false }`, no stored `miss` field.
 * `phase` distinguishes the pre-roll declared fire (where `hit`/`crit`/`rawD20`/`natural20`
 * are unknowable) from the post-roll resolved fire — this is the **double-fire-avoidance**
 * mechanism: a declared-only clause matches on `phase:"declared"` (fails at resolved, where
 * `phase` is `"resolved"`); a resolved-only clause matches on `hit`/`crit` (both `undefined` at
 * declared, so a strict-equality match against a boolean literal fails there too — see
 * {@link evaluateMatch}'s own doc).
 */
export type AttackMatch = {
  family: "attack";
  /** `"after"`: once per attack, after every target has resolved; a clause hears it only by naming it. */
  phase?: "declared" | "resolved" | "after";
  /**
   * A declared-window selector, not an event fact: the clause is also offered before a hostile
   * **activation** that names its target (`activationDeclaredReactions`), not only before an
   * attack. Battlefield Awareness's "a hostile action is taken against your target". Only read on
   * a `phase: "declared"` bystander clause (lint rule 15); {@link evaluateMatch} ignores it.
   */
  hostileActions?: true;
  hit?: boolean;
  crit?: boolean;
  ranged?: boolean;
  tech?: boolean;
  /**
   * The in-flight attack's **attack verb** — one of the seven in `@lancer-vtt/domain`'s
   * `ATTACK_VERBS` (`"ram"` | `"grapple"` | `"improvised_attack"` | `"quick_tech"` |
   * `"full_tech"` | `"fight"` | `"skirmish"`), as derived by `actionIdForAttackLike`. The
   * trigger-side twin of {@link import("./atoms").ModifierContribution.attackScope}, which
   * scopes a *modifier* to one verb. **There is no `"barrage"`**: a Barrage shot presents as
   * `"skirmish"`, so "on a Skirmish" also fires on every Barrage shot (see `ATTACK_VERBS`).
   *
   * Narrow on purpose — do not widen back to {@link MatchStringField}. As a free string, a typo
   * (`"skirmisch"`) or an unemittable verb parsed clean and silently never fired.
   *
   * Only meaningful on a `triggered` clause. A `modifier` clause's `match` omits this key
   * (`ModifierEffect.match`): the modifier fold's event carries no verb, so a verb there could
   * never match. `attackScope` is the modifier-side mechanism.
   *
   * **Distinct from {@link actionId}, which is NOT a verb** — that one carries a granted-action
   * composite id (`"ms_markerlight:0"`) and answers "which entity's action is firing."
   * Conflating them is the mistake this field exists to prevent: `actionId` *looks* like it
   * would scope by verb and would silently match nothing.
   */
  verb?: AttackVerb | AttackVerb[];
  weaponType?: MatchStringField;
  mount?: MatchStringField;
  firstAttackThisTurn?: boolean;
  natural20?: boolean;
  rawD20?: MatchScalar;
  /**
   * The firing attack's **composite** granted-action id (`"ms_markerlight:0"`) — mirrors
   * {@link ActionMatch.actionId}'s shape, evaluated against
   * {@link import("./predicate").AttackEvent.actionId} by {@link matchStringField}.
   *
   * **Composite, not bare** (the same reason the action mint itself is composite-addressed): a
   * bare id can't distinguish `ms_aggressive_system_sync:0` from `:1` — two distinct tech-attack
   * actions on one item. A clause authored on a carrier's overlay entry with `match:
   * { family: "attack", hit: true, actionId: "ms_markerlight:0" }` fires only for THAT attack,
   * never the carrier's ordinary weapon attacks — closing an over-grant this field exists to
   * prevent.
   *
   * **A DIFFERENT field from `attackScope: { verb }`** — that is a RAW-verb-space modifier-fold
   * scope, unrelated in shape and purpose; do not rename either to match the other.
   */
  actionId?: MatchStringField;
  /**
   * The **cover degree the attack's TARGET stands in** — `"none"` | `"soft"` | `"hard"`, or an
   * any-of array of them, evaluated against {@link
   * import("./predicate").AttackEvent.cover} by {@link matchStringField}. The authored half of
   * the S2.5 cover fold ({@link import("../resolve/attackModifiers").foldAttackCover}), whose
   * answer previously reached only the to-hit accuracy math and nothing authorable.
   *
   * **Degree-shaped, not a boolean and not the signed accuracy term.** The fold produces both a
   * `degree` and an `acc` (`0`/`-1`/`-2`); the degree is the one authored here. A boolean
   * (`inCover`) would collapse soft and hard, which RAW distinguishes; the signed `acc` would
   * make an entry author arithmetic (`{ "<": 0 }`) instead of naming a rules concept, and would
   * silently drift the day a third cover source shifts the number.
   *
   * **"No cover" is expressible as `cover: "none"`** - the event fact is always set to a real
   * degree wherever cover has been settled, so this is a positive match, not an absence check.
   * `npcf_snipers_mark_sniper`'s "this has no effect if the target is in cover" (Core p.309) is
   * `cover: "none"`, and a clause that wants either degree writes `cover: ["soft", "hard"]`.
   *
   * **Not knowable at the DECLARED fire** - see {@link import("./predicate").AttackEvent.cover}
   * for why (one declaration, many targets, cover not yet folded). A `cover` clause is a
   * resolved-phase / modifier-fold fact.
   */
  cover?: CoverDegree | CoverDegree[];
  /**
   * **How many characters the firing attack targets** — a {@link MatchScalar}, evaluated
   * against {@link import("./predicate").AttackEvent.targetCount} by {@link matchScalar}. The
   * authored gate for RAW's "an attack that targets more than one character" family of
   * clauses: `targetCount: { ">": 1 }`.
   *
   * **Scalar, not a boolean.** `areaAttack` (the resolution-shape fact the bonus-damage
   * halving reads) already answers "did this fan out"; what this field adds is the NUMBER, so a
   * clause can scale by it (`{ targetCount: "beyond_first" }` in a magnitude slot) and gate on
   * it from the same fact. A one-target Blast fans out (`areaAttack: true`) and matches
   * `targetCount: 1`, not `{ ">": 1 }` — that distinction is the point.
   *
   * **Not knowable at the DECLARED fire of an area attack** (the template hasn't been
   * enumerated or LoS-filtered yet) — see {@link import("./predicate").AttackEvent.targetCount}.
   * `matchScalar` reads an `undefined` actual as no match, so such a clause stays correctly
   * dark rather than silently scaling by a made-up count.
   */
  targetCount?: MatchScalar;
  /**
   * **A status this attack consumes** (`"Lock On"`) — true when the name is in {@link
   * import("./predicate").AttackEvent.consumes}, the wielder's pre-roll consume answer. The gate
   * for RAW's "an attack that consumes Lock On" family (Hacker 1, Spotter 1, Centimane 2,
   * Stormbringer 1, Drone Commander 2): `has_status(target, "Lock On")` is the wrong gate,
   * because consuming is optional and a wielder who keeps Lock On gets none of it.
   *
   * Target-dependent like `cover`: absent at the declared fire, so a declared-phase clause never
   * matches it.
   */
  consumes?: string;
  /**
   * **The named reaction this attack spends** (`"covering_fire"`, `"overwatch"`), matched against
   * {@link import("./predicate").AttackEvent.reaction}. An attack that isn't a reaction never
   * matches. Triggered clauses only: the modifier folds don't set the fact.
   */
  reaction?: MatchStringField;
};

/**
 * The damage-family `match` pattern (HP). `damage` is consumed by two mechanisms
 * (`decisions/0175`): the attack interception window (`reactionRegistry.ts` — Leadership Die,
 * Argonaut Shield, Bondmate, Stay-of-Execution-class) and, for non-attack damage, the trigger
 * bus (`resolve/triggers.ts`'s `resolveDamageTrigger`). `evaluateMatch` reads `false` whenever no
 * {@link import("./predicate").DamageEvent} backs it.
 *
 * **`ranged` is the ONE real fact, `npc-features-fielded-roster` Phase 7.2** — RAW's Rebound
 * trigger ("you take damage from a ranged attack"). Set only by `gatherReaction`'s
 * `compileAuthoredOffers`/`compileProtectorOffers`/`compileAutomaticOutcome` (`reactionRegistry.ts`)
 * off `ReactionContext.ranged`; the non-attack bus site leaves it unset, so any `ranged` match
 * fails there. Mirrors {@link AttackMatch.ranged}'s own semantics (a tech
 * attack is also not-ranged — the same complement-encoding gotcha `lint.ts` rule 5 already
 * flags for the attack family applies here too). Every other damage-family fact stays
 * unbacked/reserved for family-completeness.
 */
export type DamageMatch = {
  family: "damage";
  ranged?: boolean;
  /**
   * **Was the triggering attack a tech attack?** Set beside `ranged` at the same sites, so a melee
   * attack is `{ ranged: false, tech: false }` (Blademaster's Parry, "hit by a melee attack").
   * Unset at the non-attack bus site, like `ranged`.
   */
  tech?: boolean;
  /**
   * **Did the triggering attack hit?** RAW separates "a character hits you with a ranged attack"
   * (Gunslinger's Return Fire, `hit: true`) from "takes damage from a ranged attack" (Rebound, no
   * `hit`): a Reliable weapon's floor on a miss is damage from the attack without a hit. `true` at
   * the hit arm's window, `false` at the Reliable-miss window (`gatherReliableMissReaction`), unset
   * at the non-attack bus site, so a `hit` match never fires there.
   */
  hit?: boolean;
  /**
   * **Did the hit actually deal damage?** (`npc-features-fielded-roster` Phase 13.2, Open fork F3.)
   * `true` when the target's final damage after mitigation is above 0. Naming this field moves the
   * clause from the pre-mitigation interception window to the settled-damage pass
   * (`reactionRegistry.ts`'s `gatherSettledDamageOutcome`), the only site that backs it. Honored on
   * automatic (non-optional) clauses only; an optional offer or the non-attack bus site never sets
   * it, so a `dealt` clause does not fire there.
   */
  dealt?: boolean;
  /**
   * **Was the damage of this type?** (`npc-gaps` 2.6, Regenerator's "taken energy damage".) Holds
   * when the event's damage types include it: an attack's rolled types at its damage window, the
   * types that survived mitigation at the settled pass (`dealt`), and the emitter's one type at the
   * non-attack bus site. A Sniper's Mark structure substitution carries no type, so no `dtype`
   * clause fires on it.
   */
  dtype?: DamageType;
};

/**
 * The heat-family `match` pattern. Fired by the referee's track observer
 * (`referee/trackObserver.ts`) when a carrier's heat goes to 0 from above it: `cleared: true`
 * (Explosive Vents' "when you clear all heat"). `dangerZone` has no backing fact yet and never
 * matches.
 */
export type HeatMatch = {
  family: "heat";
  dangerZone?: boolean;
  cleared?: boolean;
};

/**
 * The track_damage-family `match` pattern (`track: structure|stress`). Fired by the referee's track
 * observer when a carrier loses Stress or Structure (Explosive Vents' "take stress", External
 * Batteries' "take any structure damage").
 */
export type TrackDamageMatch = {
  family: "track_damage";
  track?: "structure" | "stress";
};

/**
 * The destruction-family `match` pattern — destruction content that is NOT an interception
 * window. **No {@link TriggerEvent} payload backs this yet** — honestly INERT, same as
 * {@link HeatMatch}. (`on_would_be_destroyed`-CLASS interception windows themselves stay their
 * own mechanism, never a family.)
 */
export type DestructionMatch = {
  family: "destruction";
};

/**
 * The status-family `match` pattern — `change: gained|lost`, and `status` any-of-matches the
 * status name.
 */
export type StatusMatch = {
  family: "status";
  change?: "gained" | "lost";
  status?: MatchStringField;
  /** Only a loss for this reason: `expired` (its duration ended it) or `removed` (anything else). */
  reason?: "expired" | "removed";
};

/**
 * The save-family `match` pattern — `phase:"declared"` is the forced-save declaration,
 * `phase:"resolved"` the rolled outcome. `stat`/`cause` any-of-match; `dc` is a
 * {@link MatchScalar}.
 */
export type SaveMatch = {
  family: "save";
  phase?: "declared" | "resolved";
  stat?: MatchStringField;
  cause?: MatchStringField;
  dc?: MatchScalar;
};

/** The action-family `match` pattern — `actionId`, any-of, and the pre-collapse RAW `verb` ({@link ActionEvent.verb}). */
export type ActionMatch = {
  family: "action";
  actionId?: MatchStringField;
  verb?: EarmarkVerb;
};

/** The move-family `match` pattern — the declared move `distance`, a {@link MatchScalar}, and whether the mover chose it. */
export type MoveMatch = {
  family: "move";
  distance?: MatchScalar;
  /** {@link MoveEvent.voluntary}: `true` matches only a move the mover chose (Banish). */
  voluntary?: boolean;
  /**
   * `"declared"`: a watcher's interrupt, offered BEFORE the move applies (Impending Threat, Moving
   * Target: `moveInterrupts.ts`), not after it ends. Only a `bystander` clause carries it; the
   * observed-move window's event has no phase, so a declared clause never fires there.
   */
  phase?: "declared";
};

/**
 * The trample-family `match` pattern. **No {@link TriggerEvent} payload backs this yet** — a
 * clause simply omits `match` entirely to fire unconditionally on every trample fire (there is
 * exactly one meaningful trample event today: "this character's occupied cell was crossed"), so
 * an explicit `match: { family: "trample" }` is honestly INERT the same way {@link DamageMatch}
 * is — reserved for family-completeness, not a real filter. Widen this (and add a `TrampleEvent`
 * to {@link TriggerEvent}) if a future trample clause needs to discriminate on some fact of the
 * crossing (e.g. distance moved this activation).
 */
export type TrampleMatch = {
  family: "trample";
};

/**
 * The aura-family `match` pattern: the {@link AuraMoment}(s) the clause acts at, any-of. Required on
 * an `on:"aura"` clause (the schema refuses one without it), since a field acts at several moments
 * and an unscoped clause would fire at all of them.
 */
export type AuraMatch = {
  family: "aura";
  moment: AuraMoment | AuraMoment[];
};

/**
 * The four lifecycle families' `match` patterns. They carry no fields: the edge is the family
 * name, and the old `active` field is gone because the turn fire's only carrier IS the active
 * unit (so `active: true` was a tautology and `active: false` could never fire). A clause omits
 * `match` entirely; the arms exist so {@link TriggerMatch} stays family-complete.
 */
export type StartTurnMatch = { family: "start_turn" };
/** See {@link StartTurnMatch}. */
export type EndTurnMatch = { family: "end_turn" };
/** See {@link StartTurnMatch}. */
export type StartRoundMatch = { family: "start_round" };
/** See {@link StartTurnMatch}. */
export type EndRoundMatch = { family: "end_round" };

/**
 * One `match` pattern. Closed, discriminated on `family` — mirrors {@link TriggerEvent}'s own
 * discriminant exactly so {@link evaluateMatch} narrows both sides of the comparison together.
 * Every family in {@link TriggerFamily} has an arm here (family-completeness), even the four
 * (`damage`/`heat`/`track_damage`/`destruction`) with no {@link TriggerEvent} payload yet.
 */
export type TriggerMatch =
  | AttackMatch
  | DamageMatch
  | HeatMatch
  | TrackDamageMatch
  | DestructionMatch
  | StatusMatch
  | SaveMatch
  | ActionMatch
  | MoveMatch
  | TrampleMatch
  | AuraMatch
  | StartTurnMatch
  | EndTurnMatch
  | StartRoundMatch
  | EndRoundMatch;

// ── the match evaluator ─────────────────────────────────────────────────────────────

function evaluateAttackMatch(match: AttackMatch, e: AttackEvent): boolean {
  if (match.phase !== undefined && e.phase !== match.phase) return false;
  if (match.hit !== undefined && e.hit !== match.hit) return false;
  if (match.crit !== undefined && e.crit !== match.crit) return false;
  if (match.ranged !== undefined && e.ranged !== match.ranged) return false;
  if (match.tech !== undefined && e.tech !== match.tech) return false;
  if (match.verb !== undefined && !matchStringField(e.verb, match.verb)) return false;
  if (match.weaponType !== undefined && !matchStringField(e.weaponType, match.weaponType)) return false;
  if (match.mount !== undefined && !matchStringField(e.mount, match.mount)) return false;
  if (match.firstAttackThisTurn !== undefined && e.firstAttackThisTurn !== match.firstAttackThisTurn) return false;
  if (match.natural20 !== undefined && e.natural20 !== match.natural20) return false;
  if (match.rawD20 !== undefined && !matchScalar(e.rawD20, match.rawD20)) return false;
  if (match.actionId !== undefined && !matchStringField(e.actionId, match.actionId)) return false;
  if (match.cover !== undefined && !matchStringField(e.cover, match.cover)) return false;
  if (match.targetCount !== undefined && !matchScalar(e.targetCount, match.targetCount)) return false;
  if (match.consumes !== undefined && !(e.consumes ?? []).includes(match.consumes)) return false;
  if (match.reaction !== undefined && !matchStringField(e.reaction, match.reaction)) return false;
  return true;
}

/**
 * The **pre-roll-knowable subset** of an {@link AttackMatch} — `ranged`/`tech`/`weaponType`/
 * `mount`/`actionId` — evaluated against a specific weapon(+declaration)'s own static facts,
 * deliberately IGNORING every OUTCOME-dependent facet (`phase`/`hit`/`crit`/
 * `firstAttackThisTurn`/`natural20`/`rawD20`).
 *
 * The pre-declared-checkbox surface enumerates `requiresOptIn` riders BEFORE the attack roll
 * happens, so outcome facts are genuinely
 * unknowable at enumeration time — a checkbox is a **standing conditional intent** ("if it
 * lands and qualifies, do it"), not a prediction of whether it *will* fire. Only
 * {@link evaluateMatch}, run for real against a fired {@link TriggerEvent} at `fireTrigger`
 * time, settles the full match (including outcome facets and `condition`). This function is a
 * deliberately NARROWER sibling of the private `evaluateAttackMatch` above — not a variant that
 * risks silently diverging on the outcome fields, because it structurally never reads them —
 * reusing the same per-field comparators ({@link matchStringField}) `evaluateAttackMatch` uses.
 *
 * `actionId` belongs in THIS bucket, not the outcome-dependent one, even though it isn't a
 * static property of the weapon item the way `weaponType`/`mount`
 * are: it's fixed the moment the attack is DECLARED (which action id the attacker is firing as),
 * before any roll happens, so it's just as pre-roll-knowable as `ranged`/`tech` — the caller
 * (`previewOptInRiders`) supplies it via the same `attackHandlerId` derivation the real fire
 * sites use, since it's always set (falling back to the resolved weapon's OWN id for an
 * ordinary attack) rather than absent. If this function ignored it, a rider authored with
 * `match: { family: "attack", actionId: "ms_markerlight:0" }` — meant to fire ONLY for that
 * granted action — would be enumerated as a checkbox on every ordinary weapon the carrier
 * holds, re-opening at the enumeration layer exactly the over-grant the fire layer closes.
 */
export function attackMatchAdmitsWeapon(
  match: AttackMatch,
  weapon: { weaponType?: string; mount?: string; ranged: boolean; tech: boolean; actionId?: string; verb?: string },
): boolean {
  if (match.ranged !== undefined && weapon.ranged !== match.ranged) return false;
  if (match.tech !== undefined && weapon.tech !== match.tech) return false;
  if (match.verb !== undefined && !matchStringField(weapon.verb, match.verb)) return false;
  if (match.weaponType !== undefined && !matchStringField(weapon.weaponType, match.weaponType)) return false;
  if (match.mount !== undefined && !matchStringField(weapon.mount, match.mount)) return false;
  if (match.actionId !== undefined && !matchStringField(weapon.actionId, match.actionId)) return false;
  return true;
}

/**
 * Compile-time partition pin for {@link attackMatchAdmitsWeapon}.
 *
 * The function above reads a hand-listed SUBSET of {@link AttackMatch}'s fields, which makes it
 * the classic silently-diverging projection: add a new pre-roll-knowable facet to `AttackMatch`
 * (say `sizeClass`) and `attackMatchAdmitsWeapon` keeps compiling while quietly ignoring it,
 * so the checkbox surface starts advertising riders that can never fire. Nothing about a
 * runtime test catches that — the new field simply isn't exercised yet.
 *
 * So the partition is pinned in the TYPE system, over the INPUT's keys rather than the output's:
 * every `AttackMatch` field must be classified into exactly one of THREE buckets. Adding a field
 * to `AttackMatch` without listing it below fails `typecheck` with `Type 'true' is not
 * assignable to type 'never'`, forcing the author to decide which bucket it belongs to.
 *
 *  1. **pre-roll-knowable** ({@link AttackMatchPreRollKey}) — readable before the roll, off the
 *     declaration + the weapon's own static facts. Add it to the list AND to the function.
 *  2. **outcome-dependent** ({@link AttackMatchOutcomeDependentKey}) — knowable only after the
 *     d20. Adding it to the list alone is the correct fix.
 *  3. **target-dependent** ({@link AttackMatchTargetDependentKey}) — settled before the roll,
 *     but off the TARGET (or the geometry between the two), not off the weapon. This function
 *     is handed only a weapon's static facts and cannot answer it, so it is deliberately
 *     ignored here, exactly like bucket 2 — but for a different reason, and calling it
 *     "outcome-dependent" would be a lie about when the fact exists. The consequence is the
 *     same conservative over-enumeration bucket 2 already accepts: the pre-declared checkbox
 *     surface may offer a rider that the real fire then declines, which is what a checkbox
 *     already is (a standing conditional intent, not a prediction).
 */
// `verb` is pre-roll-knowable: it derives from the declaration (the weapon/tech being used
// and the attacker's kind), never from the outcome.
type AttackMatchPreRollKey = "ranged" | "tech" | "weaponType" | "mount" | "actionId" | "verb";
// `hostileActions` selects the declared window's activation pass alongside `phase`; like `phase`,
// no weapon fact answers it.
type AttackMatchOutcomeDependentKey = "phase" | "hostileActions" | "hit" | "crit" | "firstAttackThisTurn" | "natural20" | "rawD20";
// `cover` is settled pre-roll by `foldAttackCover`, but off the target's position and the
// terrain between the two combatants — nothing about the weapon item answers it, and
// `previewOptInRiders` enumerates riders per WEAPON, not per (weapon, target) pair.
// `targetCount` is settled pre-roll too, but off the TEMPLATE's catch set (and the LoS
// exclusion over it) — nothing about the weapon item answers "how many characters will this
// particular firing actually hit," and `previewOptInRiders` enumerates per WEAPON, never per
// (weapon, aim point) pair. Same bucket as `cover`, same conservative over-enumeration.
// `consumes` is the wielder's per-target consume answer (`settleConsumeChoice`), settled pre-roll
// against a target that carries the status — nothing about the weapon item answers it.
// `reaction` turns on the target too: a Heavy ranged reaction shot is Covering Fire only at a
// target carrying the attacker's Mark (`isCoveringFireShot`).
type AttackMatchTargetDependentKey = "cover" | "targetCount" | "consumes" | "reaction";
type UnclassifiedAttackMatchKey = Exclude<
  keyof Omit<AttackMatch, "family">,
  AttackMatchPreRollKey | AttackMatchOutcomeDependentKey | AttackMatchTargetDependentKey
>;
const _attackMatchKeysAreFullyPartitioned: [UnclassifiedAttackMatchKey] extends [never] ? true : never = true;
void _attackMatchKeysAreFullyPartitioned;

/**
 * Compile-time partition pin for `previewOptInActionRiders`'s key derivation (`resolve.ts`) —
 * the action-family sibling of {@link AttackMatchPreRollKey}'s pin above, over
 * {@link ActionMatch} instead of {@link AttackMatch}.
 *
 * The question this pin forces is narrower than the attack-family one: an `on:"action"` fire
 * has no roll to await, so there is no pre-roll/outcome-dependent split here — every
 * `ActionMatch` field is knowable the instant the action is declared. What matters instead is
 * whether a field participates in **key derivation**: `previewOptInActionRiders` enumerates
 * riders into a `Record<actionId, OptInRiderPreview[]>`, and today `actionId` is the ONLY field
 * read to decide which bucket(s) a rider lands in.
 *
 * `ActionMatch` has exactly one field today, so this pin is small — but its sibling above has
 * already caught the real thing: a lane adding `AttackMatch.actionId` underneath
 * `attackMatchAdmitsWeapon` without updating its partition. The same shape of bug lands here
 * the moment `ActionMatch` grows a second field. Add one (say a
 * hypothetical `origin: "granted" | "core"`) without listing it below and this fails
 * `typecheck` with `Type 'true' is not assignable to type 'never'`, forcing the author to
 * decide: does the new field determine which key(s) a rider is filed under (join
 * `ActionMatchKeyDerivedKey` and fold it into `previewOptInActionRiders`'s bucketing), or is it
 * a further RESTRICTING facet the enumerator should evaluate-and-possibly-exclude-on but never
 * key by (join `ActionMatchNonKeyKey` instead, and give `previewOptInActionRiders` an
 * `attackMatchAdmitsWeapon`-style admits-check for it)? Either way, silent omission — a new
 * field the enumerator quietly never reads — is impossible.
 */
type ActionMatchKeyDerivedKey = "actionId";
// `verb` restricts without keying: the preview buckets by ledger id, so a verb-scoped opt-in rider
// would show on its `actionId`'s row for every verb of it. No shipped opt-in clause sets `verb`.
type ActionMatchNonKeyKey = "verb";
type UnclassifiedActionMatchKey = Exclude<keyof Omit<ActionMatch, "family">, ActionMatchKeyDerivedKey | ActionMatchNonKeyKey>;
const _actionMatchKeysAreFullyPartitioned: [UnclassifiedActionMatchKey] extends [never] ? true : never = true;
void _actionMatchKeysAreFullyPartitioned;

function evaluateActionMatch(match: ActionMatch, e: ActionEvent): boolean {
  if (match.actionId !== undefined && !matchStringField(e.actionId, match.actionId)) return false;
  if (match.verb !== undefined && e.verb !== match.verb) return false;
  return true;
}

function evaluateMoveMatch(match: MoveMatch, e: MoveEvent): boolean {
  if (match.distance !== undefined && !matchScalar(e.distance, match.distance)) return false;
  if (match.voluntary !== undefined && e.voluntary !== match.voluntary) return false;
  if (match.phase !== undefined && e.phase !== match.phase) return false;
  return true;
}

function evaluateSaveMatch(match: SaveMatch, e: SaveEvent): boolean {
  if (match.phase !== undefined && e.phase !== match.phase) return false;
  if (match.stat !== undefined && !matchStringField(e.stat, match.stat)) return false;
  if (match.cause !== undefined && !matchStringField(e.cause, match.cause)) return false;
  if (match.dc !== undefined && !matchScalar(e.dc, match.dc)) return false;
  return true;
}

function evaluateStatusMatch(match: StatusMatch, e: StatusEvent): boolean {
  if (match.change !== undefined && e.change !== match.change) return false;
  if (match.status !== undefined && !matchStringField(e.status, match.status)) return false;
  if (match.reason !== undefined && e.reason !== match.reason) return false;
  return true;
}

function evaluateAuraMatch(match: AuraMatch, e: AuraEvent): boolean {
  return Array.isArray(match.moment) ? match.moment.includes(e.moment) : match.moment === e.moment;
}

/** `npc-features-fielded-roster` Phase 7.2 — the ONE real damage-family fact ({@link DamageMatch}'s own doc). */
function evaluateDamageMatch(match: DamageMatch, e: DamageEvent): boolean {
  if (match.ranged !== undefined && e.ranged !== match.ranged) return false;
  if (match.tech !== undefined && e.tech !== match.tech) return false;
  if (match.hit !== undefined && e.hit !== match.hit) return false;
  if (match.dealt !== undefined && e.dealt !== match.dealt) return false;
  if (match.dtype !== undefined && !(e.dtypes ?? []).includes(match.dtype)) return false;
  return true;
}

/**
 * Evaluate a {@link TriggerMatch} against a fired {@link TriggerEvent} payload. Pure, total.
 *
 * **Boolean fields compare by strict equality, NOT the absent-means-false convention
 * `predicate.ts` uses** (`attackEventOf(ctx)?.ranged ?? false`). This is deliberate and
 * load-bearing for **double-fire avoidance**: at the `attack`/declared fire, `hit`/`crit` are
 * `undefined` (unrolled) — a resolved-only clause's `match: { hit: true }` must NOT match
 * `undefined`, or it would fire twice (once declared, once resolved). Strict equality
 * (`undefined !== true`, `undefined !== false`) gives exactly that; an `?? false` convention
 * would wrongly match `{ hit: false }` at the declared fire, before the roll even happens.
 *
 * `event` absent, or its `family` not matching `match.family`, never matches (defensive —
 * `fireTrigger` filters by family before this runs, in the model this is built for, so a
 * mismatch here means a caller bug, not authored content; still handled as a clean `false`,
 * never a throw, matching every other closed-language evaluator in this file).
 */
export function evaluateMatch(match: TriggerMatch, event: TriggerEvent | undefined): boolean {
  switch (match.family) {
    case "attack": {
      const e = event?.family === "attack" ? event : undefined;
      return e !== undefined && evaluateAttackMatch(match, e);
    }
    case "start_turn":
    case "end_turn":
    case "start_round":
    case "end_round":
      // No fields: the family is the whole fact.
      return event?.family === match.family;
    case "action": {
      const e = event?.family === "action" ? event : undefined;
      return e !== undefined && evaluateActionMatch(match, e);
    }
    case "move": {
      const e = event?.family === "move" ? event : undefined;
      return e !== undefined && evaluateMoveMatch(match, e);
    }
    case "save": {
      const e = event?.family === "save" ? event : undefined;
      return e !== undefined && evaluateSaveMatch(match, e);
    }
    case "status": {
      const e = event?.family === "status" ? event : undefined;
      return e !== undefined && evaluateStatusMatch(match, e);
    }
    case "damage": {
      // A real `DamageEvent` reaches this arm from the reaction interception window and from
      // `resolveDamageTrigger`'s bus fire (see `DamageMatch`'s own doc); anywhere else `event` is
      // `undefined`/a different family, so this degrades to `false`.
      const e = event?.family === "damage" ? event : undefined;
      return e !== undefined && evaluateDamageMatch(match, e);
    }
    case "aura": {
      const e = event?.family === "aura" ? event : undefined;
      return e !== undefined && evaluateAuraMatch(match, e);
    }
    case "heat": {
      const e = event?.family === "heat" ? event : undefined;
      return e !== undefined && match.dangerZone === undefined && (match.cleared === undefined || match.cleared === e.cleared);
    }
    case "track_damage": {
      const e = event?.family === "track_damage" ? event : undefined;
      return e !== undefined && (match.track === undefined || match.track === e.track);
    }
    case "destruction":
    case "trample":
      // Honestly INERT (see each match type's own doc) — no TriggerEvent variant exists for
      // these families yet, so there is structurally nothing to match against. A trample clause
      // means to omit `match` entirely (see {@link TrampleMatch}'s own doc), which never reaches
      // this arm at all — `fireTrigger`'s filter skips `evaluateMatch` outright when a handler's
      // `match` is `undefined`.
      return false;
  }
}
